<!-- eslint-disable -->
<template>
  <div class="pa-4">
    <!-- Filters -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('admin.searchPosts')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="debouncedSearch"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="groupFilter"
          :items="groupOptions"
          :label="$t('admin.filterByGroup')"
          item-title="groupName"
          item-value="groupId"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="loadPosts"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- Posts List -->
    <v-card v-else v-for="post in posts" :key="post.postId" class="mb-3" variant="outlined">
      <v-card-text>
        <div class="d-flex align-start">
          <v-avatar size="40" class="mr-3" :color="'primary'">
            <span class="text-white">{{ post.username?.charAt(0).toUpperCase() }}</span>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="d-flex align-center mb-1">
              <span class="font-weight-medium">{{ post.displayName || post.username }}</span>
              <v-chip 
                v-if="post.userStatus !== 'active'" 
                :color="post.userStatus === 'blocked' ? 'error' : 'grey'"
                size="x-small"
                class="ml-2"
              >
                {{ post.userStatus }}
              </v-chip>
              <v-chip 
                v-if="post.groupName" 
                size="x-small"
                :color="post.groupIsPublic ? 'info' : 'warning'"
                class="ml-2"
              >
                <v-icon start size="x-small">{{ post.groupIsPublic ? 'mdi-earth' : 'mdi-lock' }}</v-icon>
                {{ post.groupName }}
              </v-chip>
              <v-spacer></v-spacer>
              <span class="text-caption text-grey">{{ formatDate(post.created_at) }}</span>
            </div>
            
            <p class="mb-2">{{ post.content }}</p>
            
            <!-- Images preview -->
            <div v-if="post.images || post.imageUrl" class="mb-2">
              <v-chip size="small" color="info" variant="tonal">
                <v-icon start size="small">mdi-image</v-icon>
                {{ getImageCount(post) }} {{ $t('admin.images') }}
              </v-chip>
            </div>
            
            <div class="d-flex align-center text-caption text-grey">
              <span class="mr-4">
                <v-icon size="small">mdi-comment</v-icon>
                {{ post.commentCount }} {{ $t('admin.comments') }}
              </span>
              <span>
                <v-icon size="small">mdi-heart</v-icon>
                {{ post.reactionCount }} {{ $t('admin.reactions') }}
              </span>
            </div>
          </div>
          
          <v-btn
            icon
            color="error"
            variant="text"
            size="small"
            @click="confirmDelete(post)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Empty state -->
    <v-alert v-if="!loading && posts.length === 0" type="info">
      {{ $t('admin.noPosts') }}
    </v-alert>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        @update:model-value="loadPosts"
      ></v-pagination>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-error">
          <v-icon start color="error">mdi-alert</v-icon>
          {{ $t('admin.deletePost') }}
        </v-card-title>
        <v-card-text>
          {{ $t('admin.deletePostConfirm') }}
          <v-textarea
            v-model="deleteReason"
            :label="$t('admin.reason')"
            variant="outlined"
            rows="2"
            class="mt-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="deletePost">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../../services/api'

const { t, locale } = useI18n()

const posts = ref([])
const groups = ref([])
const loading = ref(false)
const searchQuery = ref('')
const groupFilter = ref(null)
const currentPage = ref(1)
const totalPages = ref(1)
const perPage = 20

const showDeleteDialog = ref(false)
const deleteReason = ref('')
const postToDelete = ref(null)

const groupOptions = ref([])

let searchTimeout = null

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadPosts()
  }, 300)
}

const loadGroups = async () => {
  try {
    const response = await api.get('/admin/groups')
    groupOptions.value = [
      { groupId: null, groupName: t('admin.allGroups') },
      ...response.data.groups
    ]
  } catch (error) {
    console.error('Failed to load groups:', error)
  }
}

const loadPosts = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      limit: perPage
    }
    
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    if (groupFilter.value) {
      params.groupId = groupFilter.value
    }
    
    const response = await api.get('/admin/posts', { params })
    posts.value = response.data.posts || []
    totalPages.value = response.data.totalPages || 1
  } catch (error) {
    console.error('Failed to load posts:', error)
  } finally {
    loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString(locale.value)
}

const getImageCount = (post) => {
  if (post.images) {
    try {
      const images = typeof post.images === 'string' ? JSON.parse(post.images) : post.images
      return images.length
    } catch {
      return 0
    }
  }
  return post.imageUrl ? 1 : 0
}

const confirmDelete = (post) => {
  postToDelete.value = post
  deleteReason.value = ''
  showDeleteDialog.value = true
}

const deletePost = async () => {
  if (!postToDelete.value) return
  
  try {
    await api.delete('/admin/posts', {
      data: {
        postId: postToDelete.value.postId,
        reason: deleteReason.value
      }
    })
    
    // Remove from list
    posts.value = posts.value.filter(p => p.postId !== postToDelete.value.postId)
    showDeleteDialog.value = false
    postToDelete.value = null
  } catch (error) {
    console.error('Failed to delete post:', error)
    alert(error.response?.data?.error || 'Failed to delete')
  }
}

onMounted(() => {
  loadGroups()
  loadPosts()
})
</script>